const BASE = import.meta.env.VITE_PUBLIC_API_URL ?? "";

export class ApiError extends Error {
  constructor(public status: number, message: string, public payload?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}/api${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  });

  const text = await res.text();
  const data = text ? safeParse(text) : null;

  if (!res.ok) {
    let message = res.statusText || "Request failed";
    if (data && typeof data === "object" && "error" in data) {
      message = String((data as { error: unknown }).error);
    }
    throw new ApiError(res.status, message, data);
  }

  return data as T;
}

function safeParse(t: string): unknown {
  try {
    return JSON.parse(t);
  } catch {
    return t;
  }
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  del: <T,>(path: string) => request<T>(path, { method: "DELETE" }),
};

// ─── Domain types (shared shape with server) ───────────────────────────────
export interface User {
  id: number;
  email: string;
  name: string | null;
  isAdmin?: boolean;
}

export interface Member extends User {
  createdAt: string;
}

export interface Book {
  id: number;
  title: string;
  author: string;
  isbn: string | null;
  coverUrl: string | null;
  pageCount: number | null;
  summary: string | null;
  addedByUserId: number;
  addedAt: string;
  finishedAt?: string | null;
  deletedAt?: string | null;
}

export interface ChapterDescription {
  number: number;
  title: string | null;
  description: string;
}
export interface Character {
  name: string;
  role: string;
  description: string;
}
export interface Theme {
  title: string;
  description: string;
}
export interface VenueSuggestion {
  name: string;
  town: string;
  type: string;
  whyItFits: string;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  source?: "osm" | "google" | "ai_fallback";
  externalId?: string;
}

export interface ContentWarning {
  category: string;
  severity: string;
  notes: string;
}

export interface Moment {
  id: number;
  meetingId: number;
  body: string;
  capturedBy: { id: number; name: string | null; email: string } | null;
  attributedTo: { id: number; name: string | null; email: string } | null;
  createdAt: string;
}
export interface FoodDrinkPairing {
  food: { name: string; description: string }[];
  drinks: { name: string; description: string; recipe: string }[];
}

export interface ChapterQuestions {
  number: number;
  title: string | null;
  questions: string[];
}

export interface KeyPassage {
  quote: string;
  location: string;
  whyItMatters: string;
}

export interface AuthorContext {
  author: string;
  blurb: string;
  yearPublished: number | null;
  notableHonors: string[];
}

export type RelationshipIntensity = "primary" | "secondary" | "background";
export interface RelationshipPerson {
  name: string;
  role: string;
}
export interface RelationshipEdge {
  from: string;
  to: string;
  label: string;
  intensity: RelationshipIntensity;
}
export interface RelationshipGraph {
  people: RelationshipPerson[];
  edges: RelationshipEdge[];
}

export type MerchItemType =
  | "special_edition"
  | "signed_copy"
  | "apparel"
  | "tote"
  | "mug"
  | "poster"
  | "art"
  | "bookmark"
  | "other";

export interface MerchItem {
  name: string;
  type: MerchItemType | string;
  description: string;
  vendor: string;
  url: string | null;
}

export type AIContent =
  | { type: "chapters"; data: ChapterDescription[] }
  | { type: "characters"; data: Character[] }
  | { type: "themes"; data: Theme[] }
  | { type: "venue"; data: VenueSuggestion }
  | { type: "pairings"; data: FoodDrinkPairing }
  | { type: "questions"; data: ChapterQuestions[] }
  | { type: "passages"; data: KeyPassage[] }
  | { type: "context"; data: AuthorContext }
  | { type: "relationships"; data: RelationshipGraph }
  | { type: "merch"; data: MerchItem[] }
  | { type: "content_warnings"; data: ContentWarning[] };

export type AIContentType = AIContent["type"];

export interface Member {
  id: number;
  name: string | null;
  email: string;
  position: number;
  isCurrent: boolean;
}

export interface Meeting {
  id: number;
  bookId: number | null;
  bookTitle: string | null;
  meetsAt: string;
  venueName: string | null;
  venueAddress: string | null;
  notes: string | null;
  venueUpdatedBy: { id: number; name: string | null; email: string } | null;
  venueUpdatedAt: string | null;
}

export type RsvpStatus = "yes" | "no" | "maybe";

export interface Rsvp {
  userId: number;
  name: string | null;
  email: string;
  status: RsvpStatus;
  dietary: string | null;
  updatedAt: string;
}

export interface ReadingProgress {
  userId: number;
  name: string | null;
  email: string;
  currentChapter: number;
  updatedAt: string;
}

export interface ChapterNote {
  id: number;
  bookId: number;
  chapter: number;
  body: string;
  author: { id: number; name: string | null; email: string };
  isMine: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BookRating {
  userId: number;
  name: string | null;
  email: string;
  stars: number;
  review: string | null;
  ratedAt: string;
}

export interface RatingsResponse {
  ratings: BookRating[];
  average: number | null;
  count: number;
}

export interface MemberProfile {
  id: number;
  email: string;
  name: string | null;
  bio: string | null;
  favoriteGenres: string[];
  avatarHue: number;
  createdAt: string;
  stats: {
    booksPicked: number;
    notesLeft: number;
    ratingsGiven: number;
    bookmarksSaved: number;
    turnPosition: number | null;
    isCurrentPicker: boolean;
  } | null;
}

export interface Bookmark {
  id: number;
  quote: string;
  chapter: number | null;
  page: number | null;
  note: string | null;
  createdAt: string;
  author: { id: number; name: string | null; email: string };
  book: { id: number; title: string; author: string; coverUrl: string | null };
}

export interface ActivityItem {
  id: number;
  type:
    | "book_added"
    | "book_finished"
    | "book_set_current"
    | "note_added"
    | "rsvp_set"
    | "rating_added"
    | "progress_updated"
    | "bookmark_saved"
    | "meeting_scheduled"
    | "turn_advanced";
  user: { id: number; name: string | null; email: string } | null;
  book: { id: number; title: string; coverUrl: string | null } | null;
  meetingId: number | null;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

export interface Recommendation {
  title: string;
  author: string;
  yearPublished: number | null;
  blurb: string;
  whyForUs: string;
  vibeTags: string[];
}

export interface ClubStats {
  totals: {
    books: number;
    pages: number;
    finished: number;
    notes: number;
    meetings: number;
    members: number;
    bookmarks: number;
    averageRating: number | null;
    ratingCount: number;
  };
  topRated: {
    id: number;
    title: string;
    author: string;
    coverUrl: string | null;
    averageStars: number;
    ratingCount: number;
  }[];
  topPickers: {
    userId: number;
    name: string | null;
    email: string;
    picks: number;
    avgPickedRating: number | null;
  }[];
  byYear: { year: string; count: number; pages: number }[];
}

export interface PairingArchive {
  book: { id: number; title: string; author: string; coverUrl: string | null };
  generatedAt: string;
  food: { name: string; description: string }[];
  drinks: { name: string; description: string; recipe: string }[];
}

export interface AIAnnotation {
  id: number;
  bookId: number;
  aiType: string;
  kind: "flag" | "note";
  body: string;
  author: { id: number; name: string | null; email: string } | null;
  resolvedAt: string | null;
  resolvedBy: { id: number; name: string | null } | null;
  createdAt: string;
}

export interface AIOverrideMeta {
  editor: { id: number; name: string | null; email: string } | null;
  reason: string | null;
  updatedAt: string;
}

export interface AIAnnotationCounts {
  [aiType: string]: { flag: number; note: number };
}
